import { createContext, ReactNode, useEffect, useState } from 'react'
import { User, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/config/firebase'
import { initializePushNotifications } from '@/services/UnifiedPushNotificationService'

interface UserData {
  id: string
  email: string
  displayName: string
  role: 'user' | 'admin' | 'moderator'
  status: 'active' | 'suspended' | 'deleted'
}

interface AuthContextType {
  user: User | null
  userData: UserData | null
  loading: boolean
  isAdmin: boolean
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)
      
      if (firebaseUser) {
        try {
          // Fetch user data from Firestore to check admin role
          const userDocRef = doc(db, 'users', firebaseUser.uid)
          const userDoc = await getDoc(userDocRef)
          
          if (userDoc.exists()) {
            const data = userDoc.data()
            setUserData({
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: data.displayName || firebaseUser.displayName || '',
              role: data.role || 'user',
              status: data.status || 'active'
            })
          } else {
            // User document doesn't exist, create basic user data
            setUserData({
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              role: 'user',
              status: 'active'
            })
          }
          
          // Initialize push notifications after successful authentication
          initializePushNotifications().catch(error => {
            console.error('Failed to initialize push notifications:', error)
          })
        } catch (error) {
          console.error('Error fetching user data:', error)
          setUserData(null)
        }
      } else {
        setUserData(null)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const logout = async () => {
    await auth.signOut()
    setUserData(null)
  }

  const isAdmin = userData?.role === 'admin' && userData?.status === 'active'

  const value = {
    user,
    userData,
    loading,
    isAdmin,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}